import {
  CategoryType,
  CollectionType,
  GeoPointType,
  LevelEnum,
  ProductType,
  ProviderType,
  OldSubCategoryType,
  UserType,
  CategoryConstantType,
  UTMType,
} from '@/@types';
import { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import slugify from 'slugify';
import configuration from '@/configuration';
import { categories } from '@/@constants';

type FilterType = {
  genres?: OldSubCategoryType[];
  providers?: ProviderType[];
  onlyFollowed?: boolean;
  location?: GeoPointType;
};

export type OptionsType = { host?: boolean; utm?: UTMType };

export const linksByI18n = (i18n: I18n, { host = false, utm }: OptionsType = {}) => {
  const querystring = new URLSearchParams({
    ...(utm?.source && { utm_source: utm.source }),
    ...(utm?.medium && { utm_medium: utm.medium }),
    ...(utm?.campaign && { utm_campaign: utm.campaign }),
  });
  const options = { locale: i18n.locale, lower: true, strict: true };

  const buildUrl = (uri: string) => {
    const prefix = `${host ? configuration.host : ''}/${i18n.locale}`;
    if (querystring.toString()) {
      const [base, ...rest] = uri.split('?');
      return `${prefix}${base}?${querystring.toString()}&${rest.join('&')}`;
    }
    return `${prefix}${uri}`;
  };

  const settings = (path = '/') => {
    return buildUrl(`/settings${path}`);
  };

  return {
    locale: i18n.locale,
    downloadApp: () => {
      return 'https://get.peoople.co';
    },
    root: () => {
      return buildUrl('/');
    },
    explore: (category?: CategoryConstantType, filters?: FilterType, page?: number) => {
      let base = `/${i18n._(msg`explore`)}/`;
      if (!category) {
        return buildUrl(base);
      }

      base = `${base}${slugify(i18n._(category.title), options)}/`;

      if (page && page > 1) {
        base += `${i18n._(msg`Page`).toLowerCase()}/${page}/`;
      }

      if (!filters) {
        return buildUrl(base);
      }

      const query = new URLSearchParams();
      (filters.genres || []).forEach((subcategory) => {
        query.append('genres', slugify(subcategory.id, options));
      });
      (filters.providers || []).forEach((provider) => {
        query.append('providers', slugify(provider.id, options));
      });

      if (filters.onlyFollowed) {
        query.set('onlyFollowed', '1');
      }

      if (filters.location) {
        query.set('lat', filters.location.lat.toString());
        query.set('lng', filters.location.lng.toString());
      }

      return buildUrl(`${base}?${query.toString().toLowerCase()}`);
    },
    map: () => {
      return buildUrl(`/explore/map/`);
    },
    trending: (category?: CategoryConstantType, { genres = [], providers = [], onlyFollowed = false }: FilterType = {}) => {
      if (!category) {
        return buildUrl(`/trending/`);
      }
      const categoryName = slugify(i18n._(category.title), options);
      const base = `/${slugify(i18n._(msg`best-${categoryName}`), options)}/`;
      const totalFilters = genres.length + providers.length + (onlyFollowed ? 1 : 0);
      if (totalFilters === 0) {
        return buildUrl(base);
      }

      const filters: string[] = [];
      if (genres.length === 1) {
        filters.push(slugify(i18n._(genres.at(0)!.title), options));
      }
      if (providers.length === 1) {
        filters.push(slugify(providers.at(0)!.title, options));
      }

      if (filters.length > 0 && filters.length <= 2 && totalFilters <= 2) {
        return buildUrl(`${base}${filters.join('/')}/`);
      }

      const query = new URLSearchParams();
      if (onlyFollowed) {
        query.set('followed', '1');
      }
      genres.forEach((filter) => {
        query.append('subcategories', filter.id);
      });
      providers.forEach((filter) => {
        query.append('providers', filter.id);
      });
      return buildUrl(`${base}/?${query.toString()}`.toLowerCase());
    },
    product: (data: string | ProductType, recommendationId?: string) => {
      if (typeof data === 'string') {
        return buildUrl(`/product/${data}/`);
      }

      let query: string | URLSearchParams = '';
      if (recommendationId) {
        query = new URLSearchParams();
        query.set('referer', recommendationId);
        query = `?${query.toString()}`;
      }

      const category = categories[data.category.name];

      return buildUrl(`/${slugify(i18n._(category.itemTitle), options)}/${slugify(data.name, options) || i18n._(msg`product`)}/${data.id}/${query}`);
    },
    collection: (data: CollectionType) => {
      const user = data.owner || {
        id: 'unknown',
        nickname: 'nickname',
        name: '',
        level: {
          id: 0,
          name: 'Rookie',
          index: 0,
          emoji: '',
        },
      };
      const root = `/${i18n._((user.level?.id || user.level?.index || 0) >= 1 ? msg`influencers` : msg`users`)}/${user.nickname.replace(/^@/, '')}/`;
      return buildUrl(
        `${root}${slugify(i18n._(msg`collection`), options)}/${slugify(data.name || i18n._(msg`recommendation`), options)}/${data.id}/`,
      );
    },
    categoryLanding: (category: CategoryConstantType) => {
      const customTitle = [categories.movies.name, categories.series.name].includes(category.name) ? msg`Movies and Series` : null;
      return buildUrl(`/${slugify(i18n._(customTitle || category.title), options)}/`);
    },
    termsAndConditions: () => {
      return buildUrl(`/terms-and-conditions/`);
    },
    privacyPolicy: () => {
      return buildUrl(`/${slugify(i18n._(msg`Privacy Policy`), options)}/`);
    },
    profile: (user: UserType) => {
      const root = `/${i18n._((user.level?.id || user.level?.index || 0) >= 1 ? msg`influencers` : msg`users`)}/${user.nickname.replace(/^@/, '')}/`;

      return {
        root: buildUrl(root),
        level: buildUrl(`${root}${slugify(i18n._(msg`Level`), options)}/`),
        map: buildUrl(`${root}${slugify(i18n._(msg`Map`), options)}/`),
        activity: buildUrl(`${root}${slugify(i18n._(msg`Activity`), options)}/`),
      };
    },
    login: () => {
      return buildUrl(`/login/`);
    },
    logout: () => {
      return `/user/logout/`;
    },
    messages: (conversationId?: string) => {
      if (conversationId) {
        return buildUrl(`/messages/${conversationId}/`);
      }
      return buildUrl(`/messages/`);
    },
    createRecommendation: (category?: CategoryType, productId?: string) => {
      return {
        root: buildUrl(`/recommend/`),
        search: buildUrl(`/recommend/${category?.name}/`),
        create: buildUrl(`/recommend/${category?.name}/new/`),
        product: buildUrl(`/recommend/${category?.name}/${productId}/`),
      };
    },
    createCollection: () => {
      return buildUrl(`/new-collection`);
    },
    settings,
    changePassword: () => {
      return settings('/change-password/');
    },
    providers: () => {
      return settings('/providers/');
    },
    support: () => {
      const root = `/support/`;
      return {
        root: settings(root),
        peoople: settings(`${root}peoople/`),
        levelUp: settings(`${root}level-up/`),
        wallet: settings(`${root}wallet/`),
        rules: settings(`${root}roles/`),
        deleteAccount: settings(`${root}delete-account/`),
      };
    },
    notifications: () => {
      return buildUrl(`/notifications`);
    },
    users: {
      explore: (level: LevelEnum, index?: number) => {
        const explore = i18n._(msg`Explore`).toLowerCase();
        const users = i18n._(msg`Users`).toLowerCase();
        const page = i18n._(msg`Page`).toLowerCase();

        if (['Rookie', 'Creator'].includes(level)) {
          return `/${users}/${explore}/influencers`;
        }
        let url = `/${users}/${explore}/${level.toLowerCase()}`;

        if (index) {
          url += `/${page}/${index}`;
        }

        return buildUrl(url);
      },
    },
    admin: {
      root: () => {
        return buildUrl(`/cms/admin/`);
      },
      recommendations: () => {
        return buildUrl(`/cms/admin/recommendations/`);
      },
    },
  };
};
